/**
 * The entity registry (doc 16 §2). One row per entity that gets modeled.
 *
 * Footprints come from `src/content/`, never from the artist's memory — that is
 * the whole point of `statDrift`. Heights are the art decision, bounded by
 * Law L3.
 */

import buildings from '../content/buildings.json' with { type: 'json' };
import towers from '../content/towers.json' with { type: 'json' };
import { ECONOMY_STATES, TOWER_STATES, type EntitySpec } from './entity.ts';
import { SIGNATURE_ECONOMY, SIGNATURE_TOWER } from './materials.ts';
import { drawCanceller } from './entities/subscription_canceller.ts';
import { drawFan } from './entities/fan_enjoyer.ts';
import { drawLaser } from './entities/laser_pointer.ts';
import { drawOreRig } from './entities/ore_rig.ts';
import { drawStabby } from './entities/stabby.ts';
import { drawYeet } from './entities/yeet_cannon.ts';
import { drawZappy } from './entities/zappy_boi.ts';

export const REGISTRY: EntitySpec[] = [
  {
    id: 'ore_rig',
    class: 'economy',
    name: buildings.buildings.ore_rig.name,
    size: buildings.buildings.ore_rig.size,
    height: 24,
    tall: false,
    silhouetteFamily: 'derrick-a-frame',
    signature: { colour: SIGNATURE_ECONOMY, motif: 'amber head lamp, blinks while producing' },
    detailColours: ['#93a6ab'], // raw ore in the silo: the only non-ramp pixel
    states: ECONOMY_STATES,
    content: { file: 'buildings', key: 'ore_rig' },
    reviewedAt: null,
    draw: drawOreRig,
  },
  {
    id: 'stabby',
    class: 'tower',
    name: towers.towers.stabby.name,
    size: towers.towers.stabby.size,
    height: 20,
    tall: false,
    silhouetteFamily: 'harpoon-mast',
    signature: { colour: SIGNATURE_TOWER, motif: 'warm red targeting lamp, flares on fire' },
    states: TOWER_STATES,
    content: { file: 'towers', key: 'stabby' },
    reviewedAt: null,
    draw: drawStabby,
  },
  {
    id: 'zappy_boi',
    class: 'tower',
    name: towers.towers.zappy_boi.name,
    size: towers.towers.zappy_boi.size,
    height: 20,
    tall: false,
    silhouetteFamily: 'coil-orb',
    signature: { colour: SIGNATURE_TOWER, motif: 'warm red collar lamp; the orb goes hot on discharge' },
    states: TOWER_STATES,
    content: { file: 'towers', key: 'zappy_boi' },
    reviewedAt: null,
    draw: drawZappy,
  },
  {
    id: 'yeet_cannon',
    class: 'tower',
    name: towers.towers.yeet_cannon.name,
    size: towers.towers.yeet_cannon.size,
    height: 16,
    tall: false,
    silhouetteFamily: 'squat-mortar',
    signature: { colour: SIGNATURE_TOWER, motif: 'warm red loader lamp; muzzle flash on the lob' },
    states: TOWER_STATES,
    content: { file: 'towers', key: 'yeet_cannon' },
    reviewedAt: null,
    draw: drawYeet,
  },
  {
    id: 'laser_pointer',
    class: 'tower',
    name: towers.towers.laser_pointer.name,
    size: towers.towers.laser_pointer.size,
    height: 22,
    tall: false,
    silhouetteFamily: 'tripod-lens',
    signature: { colour: SIGNATURE_TOWER, motif: 'warm red aperture ring; the lens goes white on the burn' },
    states: TOWER_STATES,
    content: { file: 'towers', key: 'laser_pointer' },
    reviewedAt: null,
    draw: drawLaser,
  },
  {
    id: 'fan_enjoyer',
    class: 'tower',
    name: towers.towers.fan_enjoyer.name,
    size: towers.towers.fan_enjoyer.size,
    height: 20,
    tall: false,
    silhouetteFamily: 'duct-ring',
    signature: { colour: SIGNATURE_TOWER, motif: 'warm red hub lamp inside the ring' },
    states: TOWER_STATES,
    content: { file: 'towers', key: 'fan_enjoyer' },
    reviewedAt: null,
    draw: drawFan,
  },
  {
    id: 'subscription_canceller',
    class: 'tower',
    name: towers.towers.subscription_canceller.name,
    size: towers.towers.subscription_canceller.size,
    height: 22,
    tall: false,
    silhouetteFamily: 'tube-rack',
    signature: { colour: SIGNATURE_TOWER, motif: 'warm red arming lamp; the launched tube glows' },
    states: TOWER_STATES,
    content: { file: 'towers', key: 'subscription_canceller' },
    reviewedAt: null,
    draw: drawCanceller,
  },
];

export function specById(id: string): EntitySpec {
  const spec = REGISTRY.find((e) => e.id === id);
  if (!spec) throw new Error(`no entity spec: ${id}`);
  return spec;
}

/** Ids the doc 16 pipeline draws; everything else is placeholder art. */
export const REGISTRY_DEFS = new Set(REGISTRY.map((e) => e.id));
